import '~/polyfills';
import ThemeProvider from '@carvana/showroom-theme';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useEffect } from 'react';
import useMicroFrontend from '@/common/MicroFrontEnds';
import type { DeviceTypeProps } from '~/server-props/common/contracts';
import reduxWrapper from '~/store/configureStore';
import theme from '~/theme';
import initDataDog from '~/utilities/datadog';

import 'slick-carousel/slick/slick.css';
import '~/styles/reset.css';
import '@carvana/header/styles.css';
import '@carvana/merch-geolocation/dist/index.css';
import '@carvana/merch-search-bar/dist/index.css';
import '@carvana/merch-tile/dist/index.css';
import '@carvana/merch-tailwind/dist/colors.css';
import '@carvana/merch-tailwind/dist/fonts.css';
import '@carvana/merch-tailwind/dist/reference.css';
import '@carvana/showroom-css-theme/Theme.css';
import '@carvana/showroom-css-theme/Font.css';
import '@carvana/showroom-modal/index.css';
import '@carvana/showroom-forms/index.css';
import '@carvana/showroom-tabs/index.css';
import '@carvana/showroom-toast/index.css';
import '@carvana/showroom-tooltip/index.css';
import '~/styles/build/global.css';

// Polyfill for web-vitals npm package that allows for FID in all browsers plus more accurate reporting.
const webVitalsPolyfill = `
!function(){var e,t,n,i,r={passive:!0,capture:!0},a=new Date,o=function(){i=[],t=-1,e=null,f(addEventListener)},c=function(i,r){e||(e=r,t=i,n=new Date,f(removeEventListener),u())},u=function(){if(t>=0&&t<n-a){var r={entryType:"first-input",name:e.type,target:e.target,cancelable:e.cancelable,startTime:e.timeStamp,processingStart:e.timeStamp+t};i.forEach((function(e){e(r)})),i=[]}},s=function(e){if(e.cancelable){var t=(e.timeStamp>1e12?new Date:performance.now())-e.timeStamp;"pointerdown"==e.type?function(e,t){var n=function(){c(e,t),a()},i=function(){a()},a=function(){removeEventListener("pointerup",n,r),removeEventListener("pointercancel",i,r)};addEventListener("pointerup",n,r),addEventListener("pointercancel",i,r)}(t,e):c(t,e)}},f=function(e){["mousedown","keydown","touchstart","pointerdown"].forEach((function(t){return e(t,s,r)}))},p="hidden"===document.visibilityState?0:1/0;addEventListener("visibilitychange",(function e(t){"hidden"===document.visibilityState&&(p=t.timeStamp,removeEventListener("visibilitychange",e,!0))}),!0);o(),self.webVitals={firstInputPolyfill:function(e){i.push(e),u()},resetFirstInputPolyfill:o,get firstHiddenTime(){return p}}}();
`;

export type OwnProps = {
  dataDogSampleRate?: number;
  deviceTypeProps?: DeviceTypeProps;
  disableGTM: boolean;
  enableMfeDebug: boolean;
};

function getIsNative(props: OwnProps): boolean {
  const { isNative } = props.deviceTypeProps?.nativeDeviceType ?? {};

  return isNative ?? false;
}

function getIsSafari(props: OwnProps): boolean {
  const browserName = props.deviceTypeProps?.browserName ?? '';

  return browserName.includes('safari') || browserName.includes('ios');
}

function GoogleTagManager(props: OwnProps) {
  const isNative = getIsNative(props);
  const disableGTM = props.disableGTM;
  const includeGTM = !isNative && !disableGTM;

  return includeGTM ? (
    <>
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
  'gtm.start': new Date().getTime(),
  event: 'gtm.js'
});
`
        }}
      />

      <script
        defer
        src={`https://www.googletagmanager.com/gtm.js?id=${process.env.CVNA_APP_GTM_ID}`}
      />
    </>
  ) : null;
}

function App(props: AppProps<OwnProps>) {
  const { Component, pageProps } = props;
  const isSafari = getIsSafari(pageProps);

  useEffect(() => {
    initDataDog(pageProps.dataDogSampleRate);
  }, [pageProps.dataDogSampleRate]);

  useMicroFrontend(pageProps.enableMfeDebug);

  useEffect(() => {
    if (isSafari) import('focus-visible');
  }, [isSafari]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="initial-scale=1.0, width=device-width"
          key="viewport"
        />
        <link
          rel="icon"
          type="image/x-icon"
          href={`${process.env.CVNA_APP_CDN_URL}/icons/favicon.ico`}
          crossOrigin=""
        />
        <link
          rel="preload"
          href={`${process.env.CVNA_APP_CDN_URL}/fonts/BrandonText-Light.woff2`}
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="preload"
          href={`${process.env.CVNA_APP_CDN_URL}/fonts/BrandonText-Bold.woff2`}
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="preload"
          href={`${process.env.CVNA_APP_CDN_URL}/fonts/BrandonText-Regular.woff2`}
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="https://assets.fastly.carvana.io/fonts/BrandonforCarvana-Black.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="apple-touch-icon"
          href={`${process.env.CVNA_APP_CDN_ASSETS_PATH}/images/favicon.png`}
        />
        <link
          rel="icon"
          type="image/png"
          href={`${process.env.CVNA_APP_CDN_ASSETS_PATH}/images/favicon.png?width=16&height=16`}
          sizes="16x16"
        />
        <link
          rel="icon"
          type="image/png"
          href={`${process.env.CVNA_APP_CDN_ASSETS_PATH}/images/favicon.png?width=32&height=32`}
          sizes="32x32"
        />
        <link
          rel="icon"
          type="image/png"
          href={`${process.env.CVNA_APP_CDN_ASSETS_PATH}/images/favicon.png?width=96&height=96`}
          sizes="96x96"
        />
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{ __html: webVitalsPolyfill }}
        />
      </Head>
      <ThemeProvider theme={theme}>
        <div className="mx-auto max-w-[1800px]">
          <Component {...pageProps} />
        </div>
      </ThemeProvider>
      <GoogleTagManager {...pageProps} />
    </>
  );
}

export default reduxWrapper.withRedux(App);
